/**
 * Segregate a nested inputs structure into:
 *  - identicalInputsObj: deep-identical copy of the original but without nodes whose category matches target(s)
 *  - categorizedArray: flat list of the nodes whose category matches target(s) (preserving their subtrees)
 *
 * A "node" may have children either in `inputs` (top-level) or `options.inputs` (nested).
 *
 * @param {object|array} root - The original JSON object (or array) to process.
 * @param {string|string[]} targetCategories - Category value(s) to extract (default: 'on').
 * @param {boolean} forceExpandCategorized - If true, sets isExpanded: true for categorized items when target includes 'global' or 'od' (default: true).
 * @returns {{ identicalInputsObj: any, categorizedArray: any[] }}
 */
export function segregateByCategory(root, targetCategories, forceExpandCategorized = true) {  
  const categorizedArray = [];
  const isMatch = makeCategoryMatcher(targetCategories);
  
  // Check if we should force expand categorized items
  const shouldForceExpand = forceExpandCategorized && 
    (Array.isArray(targetCategories) ? targetCategories : [targetCategories])
      .some(cat => cat === 'global' || cat === 'od');

  // Create a new instance of the same object constructor
  const createNewInstance = (value) => {
    if (Array.isArray(value)) {
      return [...value];
    }
    if (value && typeof value === 'object') {
      return { ...value };
    }
    return value;
  };

  // Process a single node and return a cloned node or null if extracted
  function processNode(node) {
    if (!node || typeof node !== 'object') return node;

    // Special case: If hideInputs is true, filter out inner inputs that have matching categories
    // then move the entire filtered group to categorizedArray
    if (node.hideInputs === true && node.options && Array.isArray(node.options.inputs)) {
      // Create a new instance of the node
      const clone = createNewInstance(node);
      clone.options = createNewInstance(node.options);
      clone.options.inputs = createNewInstance(node.options.inputs);
      
      // Process children and remove only those with matching categories
      for (let i = clone.options.inputs.length - 1; i >= 0; i--) {
        const childNode = clone.options.inputs[i];
        
        // If the inner input has a category that matches target, remove it (but don't add to categorizedArray)
        if (childNode.category && isMatch(childNode.category)) {
          clone.options.inputs.splice(i, 1); // Remove from the group
        } else {
          // Process the child node normally for further nesting (no category or non-matching category)
          const processed = processNode(childNode);
          if (processed === null) {
            clone.options.inputs.splice(i, 1);
          } else {
            clone.options.inputs[i] = processed;
          }
        }
      }
      
      // Move the entire filtered group to categorizedArray and remove from main structure
      if (shouldForceExpand && clone.options) {
        clone.options.isExpanded = true;
      }
      categorizedArray.push(clone);
      return null;
    }

    // If the node itself matches, extract it and omit from the cloned structure
    if (isMatch(node.category)) {
      const clone = createNewInstance(node);
      if (shouldForceExpand && clone.options) {
        clone.options.isExpanded = true;
      }
      categorizedArray.push(clone);
      return null;
    }

    // Create a new instance of the node, then modify its child containers
    const clone = createNewInstance(node);

    // Case 1: children under node.inputs (top-level arrays like your root)
    if (Array.isArray(node.inputs)) {
      clone.inputs = createNewInstance(node.inputs);
      // Process children and remove categorized ones
      for (let i = clone.inputs.length - 1; i >= 0; i--) {
        const processed = processNode(clone.inputs[i]);
        if (processed === null) {
          clone.inputs.splice(i, 1); // Delete categorized input
        } else {
          clone.inputs[i] = processed;
        }
      }
    }

    // Case 2: children under node.options.inputs (nested)
    if (node.options && typeof node.options === 'object' && !node.hideInputs) {
      clone.options = createNewInstance(node.options);
      if (Array.isArray(node.options.inputs)) {
        clone.options.inputs = createNewInstance(node.options.inputs);
        // Process children and remove categorized ones
        for (let i = clone.options.inputs.length - 1; i >= 0; i--) {
          const processed = processNode(clone.options.inputs[i]);
          if (processed === null) {
            clone.options.inputs.splice(i, 1); // Delete categorized input
          } else {
            clone.options.inputs[i] = processed;
          }
        }
      }
    }

    return clone;
  }

  // Handle root that can be object or array
  let identicalInputsObj;
  if (Array.isArray(root)) {
    identicalInputsObj = createNewInstance(root);
    // Process items and remove categorized ones
    for (let i = identicalInputsObj.length - 1; i >= 0; i--) {
      const processed = processNode(identicalInputsObj[i]);
      if (processed === null) {
        identicalInputsObj.splice(i, 1); // Delete categorized input
      } else {
        identicalInputsObj[i] = processed;
      }
    }
  } else {
    // If root is a "node", process as such
    const processed = processNode(root);
    // If the root itself matches, we omit it from identicalInputsObj; return an empty container
    identicalInputsObj = processed === null ? (Array.isArray(root) ? [] : {}) : processed;
  }
  
  categorizedArray.reverse(); // Optional: maintain original order
  return { identicalInputsObj, categorizedArray };

  // --- helpers ---
  function makeCategoryMatcher(target) {
    const set = new Set(Array.isArray(target) ? target : [target]);
    return function matches(categoryField) {
      if (!categoryField) return false;
      const arr = Array.isArray(categoryField) ? categoryField : [categoryField];
      return arr.some((c) => set.has(c));
    };
  }
}